import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message } from 'antd'

export const MAX_TEMPORARY_MATERIALS = 30

// 素材仅用于本次预览，独立于业务图片和提交数据。
export default function useTemporaryMaterials(enabled, image) {
    const [materials, setMaterials] = useState([])
    const [selectedId, setSelectedId] = useState(null)
    const materialsRef = useRef([])
    const urlsRef = useRef(new Set())

    const releaseUrls = useCallback(() => {
        urlsRef.current.forEach(url => URL.revokeObjectURL(url))
        urlsRef.current.clear()
    }, [])
    const clear = useCallback(() => {
        releaseUrls()
        materialsRef.current = []
        setMaterials([])
        setSelectedId(null)
    }, [releaseUrls])
    useEffect(() => releaseUrls, [releaseUrls])
    useEffect(() => {
        if (!enabled) clear()
    }, [enabled, clear])

    const add = useCallback((files) => {
        if (!enabled) return
        const validFiles = []
        for (const file of Array.from(files || [])) {
            if (!file.type.startsWith('image/') || !file.size) {
                message.warning(file.name + ' 不是有效的图片文件')
                continue
            }
            validFiles.push(file)
        }
        const remaining = Math.max(0, MAX_TEMPORARY_MATERIALS - materialsRef.current.length)
        if (validFiles.length > remaining) message.warning(`临时素材最多上传${MAX_TEMPORARY_MATERIALS}张，超出部分已忽略`)
        const added = []
        for (const file of validFiles.slice(0, remaining)) {
            const url = URL.createObjectURL(file)
            urlsRef.current.add(url)
            added.push({ id: url, url, label: file.name, _temporaryMaterial: true })
        }
        if (added.length) {
            materialsRef.current = [...materialsRef.current, ...added]
            setMaterials(materialsRef.current)
        }
    }, [enabled])

    const remove = useCallback((id) => {
        if (urlsRef.current.delete(id)) URL.revokeObjectURL(id)
        materialsRef.current = materialsRef.current.filter(item => item.id !== id)
        setMaterials(materialsRef.current)
        setSelectedId(prev => prev === id ? null : prev)
    }, [])

    const previewImage = useMemo(() => {
        if (!enabled || !materials.length) return image
        const extraImages = image.extraImages || []
        const selectedIndex = materials.findIndex(item => item.id === selectedId)
        return {
            ...image,
            extraImages: [...extraImages, ...materials],
            _activeExtraIndex: selectedIndex >= 0
                ? extraImages.length + selectedIndex
                : Math.min(image._activeExtraIndex || 0, Math.max(0, extraImages.length - 1)),
        }
    }, [enabled, image, materials, selectedId])

    return { previewImage, add, remove, clear, select: setSelectedId }
}
